import { useQuery } from '@tanstack/react-query';
import { fulfillmentApi } from '../../api/fulfillment-api';
import type { FieldWorkRequest } from '../../domain/fieldWorkRequest';
import type { FulfillmentOrder } from '../../domain/fulfillment';

/** A shipment for a request: booked on the request, linked to its pick lists, or both. */
export interface RequestShipment {
  shipmentId: string;
  onRequest: boolean;
  pickLists: FulfillmentOrder[];
}

/**
 * Every shipment for a request, the one booked on the request first, then
 * the pick lists' in their order. A shipment booked or linked on a pick
 * list lives on the pick list, so the request reads it from there rather
 * than keeping a copy that could drift.
 */
export function requestShipments(
  request: Pick<FieldWorkRequest, 'logistics'>,
  pickLists: readonly FulfillmentOrder[],
): RequestShipment[] {
  const shipments = new Map<string, RequestShipment>();
  const own = request.logistics?.shipmentId;
  if (own) shipments.set(own, { shipmentId: own, onRequest: true, pickLists: [] });
  for (const order of pickLists) {
    if (!order.shipmentId) continue;
    const existing = shipments.get(order.shipmentId);
    if (existing) existing.pickLists.push(order);
    else
      shipments.set(order.shipmentId, {
        shipmentId: order.shipmentId,
        onRequest: false,
        pickLists: [order],
      });
  }
  return [...shipments.values()];
}

/** The pick lists packed for a request; the Logistics section reads their shipments too. */
export function useRequestPickLists(requestId: string) {
  return useQuery({
    queryKey: ['fulfillment-orders', { fieldWorkRequestId: requestId }],
    queryFn: () => fulfillmentApi.listOrders({ fieldWorkRequestId: requestId }),
  });
}
